import type { CSSProperties, PropsWithChildren } from "react";

import { styled } from "@linaria/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

export enum TextVariant {
  PRIMARY = "PRIMARY",
  PRIMARY_ALT = "PRIMARY_ALT",
  SECONDARY = "SECONDARY",
  SECONDARY_ALT = "SECONDARY_ALT",
  TERTIARY = "TERTIARY",
  TERTIARY_ALT = "TERTIARY_ALT",
  SUCCESS = "SUCCESS",
  WARNING = "WARNING",
  ERROR = "ERROR",
  PINK = "PINK",
  ORANGE = "ORANGE",
  YELLOW = "YELLOW",
  LIME = "LIME",
  BLUE = "BLUE",
  TEAL = "TEAL",
  PURPLE = "PURPLE",
  DISABLED = "DISABLED",
}

export enum TextSize {
  CAPTION = "CAPTION",
  BODY_SM = "BODY_SM",
  BODY_MD = "BODY_MD",
  BODY_LG = "BODY_LG",
  HEADING_SM = "HEADING_SM",
  HEADING_MD = "HEADING_MD",
  HEADING_LG = "HEADING_LG",
}

export enum TextWeight {
  REGULAR = "REGULAR",
  MEDIUM = "MEDIUM",
}

const StyledText = withTheme(styled.p<
  PropsWithTheme<{
    $size: TextSize;
    $variant: TextVariant;
    $weight: TextWeight;
    $align: CSSProperties["textAlign"];
    $cursor?: CSSProperties["cursor"];
    $isMonospace: boolean;
    $isSelectable: boolean;
    $isUnderlined: boolean;
    $isEllipsis: boolean;
    $hex?: string;
  }>
>`
  color: ${({ theme, $variant, $hex }) => {
    if ($hex) {
      return $hex;
    }

    return match($variant)
      .with(TextVariant.PRIMARY, () => theme.color.text.primary)
      .with(TextVariant.PRIMARY_ALT, () => theme.color.text.primaryAlt)
      .with(TextVariant.SECONDARY, () => theme.color.text.secondary)
      .with(TextVariant.SECONDARY_ALT, () => theme.color.text.secondaryAlt)
      .with(TextVariant.TERTIARY, () => theme.color.text.tertiary)
      .with(TextVariant.TERTIARY_ALT, () => theme.color.text.tertiaryAlt)
      .with(TextVariant.SUCCESS, () => theme.color.text.success)
      .with(TextVariant.ERROR, () => theme.color.text.error)
      .with(TextVariant.LIME, () => theme.color.text.lime)
      .with(TextVariant.PURPLE, () => theme.color.text.purple)
      .with(TextVariant.TEAL, () => theme.color.text.teal)
      .with(TextVariant.YELLOW, () => theme.color.text.yellow)
      .with(TextVariant.ORANGE, () => theme.color.text.orange)
      .with(TextVariant.PINK, () => theme.color.text.pink)
      .with(TextVariant.BLUE, () => theme.color.text.blue)
      .with(TextVariant.WARNING, () => theme.color.text.warning)
      .with(TextVariant.DISABLED, () => theme.color.text.disabled)
      .exhaustive();
  }};
  font-family: ${({ theme, $isMonospace }) =>
    $isMonospace ? theme.font.mono.family : theme.font.sans.family};
  font-size: ${({ theme, $size }) => {
    return match($size)
      .with(TextSize.CAPTION, () => theme.font.sans.size.caption)
      .with(TextSize.BODY_SM, () => theme.font.sans.size.body_sm)
      .with(TextSize.BODY_MD, () => theme.font.sans.size.body_md)
      .with(TextSize.BODY_LG, () => theme.font.sans.size.body_lg)
      .with(TextSize.HEADING_SM, () => theme.font.sans.size.heading_sm)
      .with(TextSize.HEADING_MD, () => theme.font.sans.size.heading_md)
      .with(TextSize.HEADING_LG, () => theme.font.sans.size.heading_lg)
      .exhaustive();
  }};
  font-weight: ${({ theme, $weight }) => {
    return match($weight)
      .with(TextWeight.REGULAR, () => theme.font.sans.weight.regular)
      .with(TextWeight.MEDIUM, () => theme.font.sans.weight.medium)
      .exhaustive();
  }};
  line-height: ${({ theme, $size }) => {
    return match($size)
      .with(TextSize.CAPTION, () => theme.font.sans.height.caption)
      .with(TextSize.BODY_SM, () => theme.font.sans.height.body_sm)
      .with(TextSize.BODY_MD, () => theme.font.sans.height.body_md)
      .with(TextSize.BODY_LG, () => theme.font.sans.height.body_lg)
      .with(TextSize.HEADING_SM, () => theme.font.sans.height.heading_sm)
      .with(TextSize.HEADING_MD, () => theme.font.sans.height.heading_md)
      .with(TextSize.HEADING_LG, () => theme.font.sans.height.heading_lg)
      .exhaustive();
  }};
  letter-spacing: ${({ theme, $size }) => {
    return match($size)
      .with(TextSize.CAPTION, () => theme.font.sans.spacing.caption)
      .with(TextSize.BODY_SM, () => theme.font.sans.spacing.body_sm)
      .with(TextSize.BODY_MD, () => theme.font.sans.spacing.body_md)
      .with(TextSize.BODY_LG, () => theme.font.sans.spacing.body_lg)
      .with(TextSize.HEADING_SM, () => theme.font.sans.spacing.heading_sm)
      .with(TextSize.HEADING_MD, () => theme.font.sans.spacing.heading_md)
      .with(TextSize.HEADING_LG, () => theme.font.sans.spacing.heading_lg)
      .exhaustive();
  }};

  text-align: ${({ $align }) => $align ?? "left"};
  text-decoration: ${({ $isUnderlined }) => ($isUnderlined ? "underline" : "none")};

  cursor: ${({ $cursor }) => $cursor ?? "inherit"};

  user-select: ${({ $isSelectable }) => ($isSelectable ? "text" : "none")};
  -webkit-user-select: ${({ $isSelectable }) => ($isSelectable ? "text" : "none")};
  -moz-user-select: ${({ $isSelectable }) => ($isSelectable ? "text" : "none")};
  -ms-user-select: ${({ $isSelectable }) => ($isSelectable ? "text" : "none")};

  overflow: ${({ $isEllipsis }) => ($isEllipsis ? "hidden" : "visible")};
  text-overflow: ${({ $isEllipsis }) => ($isEllipsis ? "ellipsis" : "clip")};
  white-space: ${({ $isEllipsis }) => ($isEllipsis ? "nowrap" : "normal")};
  width: ${({ $isEllipsis }) => ($isEllipsis ? "100%" : "auto")};
  overscroll-behavior: auto;

  transition: color 0.075s ease-in-out;
`);

export interface TextProps {
  variant?: TextVariant;
  size?: TextSize;
  weight?: TextWeight;
  align?: CSSProperties["textAlign"];
  cursor?: CSSProperties["cursor"];
  hex?: string;
  isMonospace?: boolean;
  isSelectable?: boolean;
  isUnderlined?: boolean;
  isEllipsis?: boolean;
}

const Text = ({
  children,
  size = TextSize.BODY_MD,
  variant = TextVariant.PRIMARY,
  weight = TextWeight.REGULAR,
  align = "left",
  cursor,
  isMonospace = false,
  isSelectable = false,
  isUnderlined = false,
  isEllipsis = false,
  hex,
}: PropsWithChildren<TextProps>) => {
  return (
    <StyledText
      $size={size}
      $variant={variant}
      $weight={weight}
      $align={align}
      $cursor={cursor}
      $isMonospace={isMonospace}
      $isSelectable={isSelectable}
      $isUnderlined={isUnderlined}
      $isEllipsis={isEllipsis}
      $hex={hex}
    >
      {children}
    </StyledText>
  );
};

export default Text;
